

import { useState, useEffect, useTransition, useCallback } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';

const fetchData = async (url, options) => {
  const response = await fetch(url, options);
  return response.json();
};

const ExportReportButton = ({ accessToken, reportId, bookmarkEvent }) => {
  const [exportId, setExportId] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [percentComplete, setPercentComplete] = useState(0);
  const [clicked, setClicked] = useState(false);
  const [isDownloaded, setDownloaded] = useState(false);
  const [reportconfig, SetReportConfig] = useState(null);
  const [isPending, startTransition] = useTransition();

  // useEffect(() => {
  //   SetReportConfig({
  //     reportLevelFilters: filters,
  //   });
  //   console.log('Input changed:', filters);
  // }, [filters]);

  const exportToFile = async () => {
    const fileRequestParams = await bookmarkEvent();
    try {
      setIsLoading(true);
      const exportTofile = await fetchData(
        `https://api.powerbi.com/v1.0/myorg/groups/5d70d713-0d4c-427a-9d23-812fde4a8bea/reports/${reportId}/ExportTo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(fileRequestParams.exportRequest),
        }
      );

      startTransition(() => setExportId(exportTofile.id));
    } finally {
      setClicked(false);
    }
  };

  const checkExportStatus = useCallback(async () => {
    if (exportId) {
      try {
        const trackExport = await fetchData(
          `https://api.powerbi.com/v1.0/myorg/groups/5d70d713-0d4c-427a-9d23-812fde4a8bea/reports/${reportId}/exports/${exportId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
  
        if (trackExport.percentComplete !== 100) {
          // Use setTimeout to avoid potential stack overflow due to synchronous recursion
          setTimeout(() => checkExportStatus(), 1000); // Check again after 1 second
          return; // Exit the function to prevent further execution
        }
  
        startTransition(() => {
          console.log('Updating status:', trackExport.status);
          setStatus(trackExport.status);
          setPercentComplete(trackExport.percentComplete);
        });
      } catch (error) {
        console.error('Error checking export status:', error);
      }
    }
  }, [accessToken, exportId, reportId, startTransition]);
  

  const getFile = useCallback(async () => {
  
    if (status === 'Succeeded' && percentComplete === 100) {
      console.log('Conditions met. Downloading file...');
  
      try {
        const downloadFile = await fetch(
          `https://api.powerbi.com/v1.0/myorg/groups/5d70d713-0d4c-427a-9d23-812fde4a8bea/reports/${reportId}/exports/${exportId}/file`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Cache-Control': 'no-cache, no-store',
              'Expires': '0',
              'Pragma': 'no-cache'
            },
          }
        );
  
        if (downloadFile.ok) {
          const blobData = await downloadFile.blob();
          handleDownload(blobData);
        } else {
          console.error('File download failed. Server response:', downloadFile);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
        setDownloaded(true);
      }
    }
  
  }, [accessToken, exportId, isDownloaded, percentComplete, reportId, status]);
  

  const handleDownload = (data) => {
    if (data) {
      const blobData = new Blob([data], { type: 'image/png' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blobData);
      link.download = 'TDCSI Benchmarking.png';

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(link.href);
      setClicked(false);
      setExportId(null);
      setDownloaded(true);
      setPercentComplete(0);
    }
  };

  const handleExportClick = useCallback(() => {
    startTransition(() => {
      exportToFile();
    });
  }, [startTransition, exportToFile]);

  useEffect(() => {
    startTransition(() => {
      checkExportStatus();
    });
  }, [startTransition, checkExportStatus]);

  useEffect(() => {
    console.log('getFile useEffect triggered with dependencies:', exportId, status, percentComplete, isDownloaded);
    if (exportId && percentComplete === 100) {
      getFile();
    }
    
  }, [getFile, exportId, status, percentComplete, isDownloaded]);

  return (
    < >
      {isLoading ? 'Exporting...' : <span  className={`cursor-pointer hover:underline`} onClick={() => handleExportClick()}>Export to Image</span>}
      <PhotoIcon
        onClick={() => !isLoading && handleExportClick()}
        className={`w-6 h-6 p2 hover:text-slate-300 cursor-pointer ${
          isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
        disabled={isLoading}
      />

      {isLoading && (
        <div className="mt-2 h-4 bg-blue-300 rounded">
          <div
            className="h-full bg-blue-500 rounded"
            style={{ width: '100%' }}
          ></div>
        </div>
      )}
    </>
  );
};

export default ExportReportButton;
